import React from 'react';

/**
 * @param {Object} props
 * @param {Function} props.func - the function that you want to execute after the debounce time.
 * @param {number} props.wait - the amount of time you want the debounce function to wait.
 * @param {Array} props.dependencies - just like useEffect dependenies.
 * @returns {Function} [string, function] returns Array of value and setValue;
 */
const useDebounce = ({ func, wait: timeout = 150, dependencies = [] }) => {
  const timeoutRef = React.useRef();

  React.useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, [...dependencies]);

  return () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(func, timeout);
  };
};

/**
 * @param {Object} props
 * @param {Function} props.func - the function that you want to execute after the debounce time.
 * @param {number} props.wait - the amount of time you want the debounce function to wait.
 * @param {string} props.initialValue - the initial value for input.
 * @param {boolean} props.resetDebounce - set to true to reset local state value held by hook & clear timeout.
 * @param {boolean} props.updateOnInitialValueChange - set to true update value on initialValue update.
 * @returns {Array} [string, function] returns Array of value and setValue;
 */
const useDebounceForInput = ({
  func,
  wait: timeout = 150,
  initialValue = '',
  resetDebounce = false,
  updateOnInitialValueChange = false,
}) => {
  const [value, setValue] = React.useState(initialValue);
  const timeoutRef = React.useRef();

  React.useEffect(() => {
    if (updateOnInitialValueChange) {
      setValue(initialValue);
    }
  }, [initialValue]);

  React.useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  React.useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      func(value);
    }, timeout);
  }, [value]);

  React.useEffect(() => {
    if (resetDebounce) {
      clearTimeout(timeoutRef.current);
      setValue('');
    }
  }, [resetDebounce]);

  return [value, setValue];
};

/**
 * Tracks when an element scrolls out of view
 *
 * ex.
 *  const [nameRef, isNameOutOfView] = useOutOfViewObserver({ threshold: 0.4 });
 *
 *  return (<div>
 *            <div ref=name>Name</div>
 *            {isNameOutOfView && <div>Name Hidden</div>}
 *          </div>)
 *
 * @param threshold number triggered when the element is less than this % in view
 * @returns {[React.MutableRefObject<HTMLElement>,boolean]}
 */
export const useOutOfViewObserver = ({ threshold = 0.2 } = {}) => {
  const ref = React.useRef();
  const [isOutOfView, setIsOutOfView] = React.useState(false);

  React.useEffect(() => {
    if (!ref.current) return;

    const onIntersection = (entries) => {
      entries.forEach((entry) => {
        setIsOutOfView(entry.intersectionRatio < threshold);
      });
    };

    const observer = new IntersectionObserver(onIntersection, {
      threshold,
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, ref.current]);

  return [ref, isOutOfView];
};

export { useDebounce, useDebounceForInput };
